import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Percent, PackageCheck, Building2, HeartHandshake } from "lucide-react";
import SEO from "@/components/SEO";

const PERKS = [
  { icon: Percent, title: "Volume pricing", body: "Wholesale rates that reward consistency and scale." },
  { icon: PackageCheck, title: "Predictable supply", body: "Reliable production schedule so your case stays full." },
  { icon: Building2, title: "Custom private label", body: "Custom sleeves, boxes and packaging for premium venues." },
  { icon: HeartHandshake, title: "Dedicated account manager", body: "One direct contact for menu planning, holidays and specials." },
];

const STEPS = [
  { n: "01", title: "Browse the collection", body: "Explore our desserts in 3″ Mini and 10″ Whole formats." },
  { n: "02", title: "Build your quote cart", body: "Add products and quantities. No account required." },
  { n: "03", title: "Send your business details", body: "Company info, volume, delivery window and special requests." },
  { n: "04", title: "Get a personalized quote", body: "Pricing and lead time returned within one business day." },
];

export default function Wholesale() {
  return (
    <div data-testid="wholesale-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <SEO
        title="B2B Wholesale — Dessert program for hospitality"
        description="Volume pricing, predictable supply, private label and dedicated account managers — build your wholesale dessert program with Cake World USA."
      />
      <div className="max-w-3xl">
        <div className="eyebrow">B2B Solutions</div>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl text-brand-ink leading-none">A premium dessert program, ready to plate.</h1>
        <p className="mt-6 text-brand-stone leading-relaxed text-lg font-ui">
          Cake World supplies hotels, restaurants and cafés with high-end desserts that arrive ready to serve — no in-house pastry team required.
        </p>
      </div>

      <div className="dot-divider mt-12" />

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {PERKS.map((p, i) => (
          <div key={p.title} data-testid={`wholesale-perk-${i}`} className="bg-white rounded-3xl border border-brand-line p-6 shadow-soft">
            <div className="w-12 h-12 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center">
              <p.icon className="w-5 h-5" />
            </div>
            <h3 className="mt-4 font-display text-xl text-brand-ink">{p.title}</h3>
            <p className="mt-2 text-sm text-brand-stone leading-relaxed font-ui">{p.body}</p>
          </div>
        ))}
      </div>

      <section className="mt-24">
        <div className="max-w-2xl">
          <div className="eyebrow">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl text-brand-ink mt-3 leading-tight">From catalog to first delivery in days.</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} data-testid={`wholesale-step-${s.n}`} className="bg-brand-ink text-white rounded-3xl p-6">
              <div className="font-display text-6xl text-brand-pink/40">{s.n}</div>
              <h3 className="mt-3 font-display text-xl">{s.title}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed font-ui">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-24 bg-brand-pinkSoft rounded-3xl border border-brand-line p-10 md:p-14 shadow-soft">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="eyebrow">Ideal partners</div>
            <h2 className="mt-3 font-display text-4xl text-brand-ink leading-tight">Who we work with.</h2>
            <p className="mt-4 text-brand-stone font-ui">
              We focus on hospitality operations that take dessert seriously — from single-location cafés to multi-property hotel groups.
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-3 text-sm font-ui text-brand-ink">
            {["Hotels", "Restaurants", "Cafés", "Resorts & Spas", "Catering businesses", "Corporate hospitality"].map((x) => (
              <li key={x} className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-brand-line">
                <Check className="w-4 h-4 text-brand-red" /> {x}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-10">
          <Link to="/request-quote" data-testid="wholesale-cta" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition">
            Start your quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
